import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentApi, couponApi } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, User, AlertCircle, Check, X, Loader2, Star, ArrowLeft, ArrowRight, HardDrive, Mail, FolderPlus, Tag, Ticket, Gift } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DOMAIN = '@mypatriotmail.org';
const REFERRAL_COOKIE_NAME = 'mpm_referral_code';
const REFERRAL_COOKIE_DAYS = 30;

// Cookie utility functions
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, error } = useAuth();
  
  // Check if plan was passed from landing page
  const initialPlanId = location.state?.planId || null;
  const initialBillingCycle = location.state?.billingCycle || 'monthly';
  
  const [step, setStep] = useState(initialPlanId ? 2 : 1); // 1: Select Plan, 2: Create Account
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState(initialBillingCycle);
  const [skipTrial, setSkipTrial] = useState(false); // Skip trial and go straight to paid
  const [referralCode, setReferralCode] = useState('');
  const [referralCodeManual, setReferralCodeManual] = useState('');
  const [referralApplied, setReferralApplied] = useState(false);
  const [referralSource, setReferralSource] = useState(''); // 'url', 'cookie', or 'manual'
  
  const [formData, setFormData] = useState({
    username: '',
    displayName: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameMessage, setUsernameMessage] = useState('');
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');

  // Handle referral code from URL or cookie on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refFromUrl = params.get('ref');
    
    if (refFromUrl) {
      // Store referral code in cookie for 30 days
      setCookie(REFERRAL_COOKIE_NAME, refFromUrl, REFERRAL_COOKIE_DAYS);
      setReferralCode(refFromUrl);
      setReferralApplied(true);
      setReferralSource('url');
      console.log(`Referral code ${refFromUrl} stored in cookie for ${REFERRAL_COOKIE_DAYS} days`);
    } else {
      // Check if there's a referral code in cookie
      const refFromCookie = getCookie(REFERRAL_COOKIE_NAME);
      if (refFromCookie) {
        setReferralCode(refFromCookie);
        setReferralApplied(true);
        setReferralSource('cookie');
        console.log(`Referral code ${refFromCookie} loaded from cookie`);
      }
    }
  }, [location.search]);

  // Apply manual referral code
  const handleApplyReferral = () => {
    if (!referralCodeManual.trim()) return;
    const code = referralCodeManual.trim().toUpperCase();
    setReferralCode(code);
    setReferralApplied(true);
    setReferralSource('manual');
    // Also save to cookie
    setCookie(REFERRAL_COOKIE_NAME, code, REFERRAL_COOKIE_DAYS);
  };

  // Clear referral code
  const clearReferral = () => {
    setReferralCode('');
    setReferralCodeManual('');
    setReferralApplied(false);
    setReferralSource('');
    // Clear the cookie
    document.cookie = `${REFERRAL_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  };

  // Validate coupon

  // Validate coupon
  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponValidating(true);
    setCouponError('');
    setAppliedCoupon(null);
    
    try {
      const planPrice = selectedPlan ? 
        (billingCycle === 'yearly' ? selectedPlan.yearly_price : selectedPlan.monthly_price) : null;
      
      const result = await couponApi.validateCoupon(
        couponCode.trim(),
        selectedPlan?.id,
        planPrice
      );
      
      if (result.valid) {
        setAppliedCoupon(result);
      }
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponValidating(false);
    }
  };
  
  const clearCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
  };

  // Fetch plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const activePlans = await paymentApi.getActivePlans();
        setPlans(activePlans);
        
        // If plan was passed from landing page, find and select it
        if (initialPlanId) {
          const plan = activePlans.find(p => p.id === initialPlanId);
          if (plan) {
            setSelectedPlan(plan);
          }
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [initialPlanId]);

  // Debounced username availability check
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.length < 2) {
      setUsernameAvailable(null);
      setUsernameMessage('');
      return;
    }

    const usernameRegex = /^[a-zA-Z0-9._-]+$/;
    if (!usernameRegex.test(username)) {
      setUsernameAvailable(false);
      setUsernameMessage('Only letters, numbers, dots, underscores, and hyphens allowed');
      return;
    }

    setCheckingAvailability(true);
    try {
      const response = await axios.get(`${API}/auth/check-username`, {
        params: { username: username.toLowerCase() }
      });
      
      setUsernameAvailable(response.data.available);
      setUsernameMessage(response.data.message);
    } catch (err) {
      setUsernameAvailable(null);
      setUsernameMessage('');
    } finally {
      setCheckingAvailability(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username) {
        checkUsernameAvailability(formData.username);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username, checkUsernameAvailability]);

  const passwordRequirements = [
    { met: formData.password.length >= 8, text: 'At least 8 characters' },
    { met: formData.password === formData.confirmPassword && formData.confirmPassword, text: 'Passwords match' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'username') {
      let cleanValue = value.toLowerCase().replace(/@.*$/, '').replace(/[^a-zA-Z0-9._-]/g, '');
      setFormData({ ...formData, username: cleanValue });
      setUsernameAvailable(null);
      setUsernameMessage('');
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!formData.username || formData.username.length < 2) {
      setLocalError('Username must be at least 2 characters');
      return;
    }

    if (usernameAvailable === false) {
      setLocalError('Please choose a different username');
      return;
    }

    if (formData.password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    const fullEmail = `${formData.username.toLowerCase()}${DOMAIN}`;

    setLoading(true);
    const result = await register(fullEmail, formData.password, formData.displayName, referralCode || null);
    
    if (result.success) {
      // If skip trial is enabled and a paid plan is selected, go directly to checkout
      if (skipTrial && selectedPlan && selectedPlan.monthly_price > 0) {
        try {
          // Token was just set by register() but axios header hasn't updated yet
          const freshToken = localStorage.getItem('token');
          const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
            plan_id: selectedPlan.id,
            billing_cycle: billingCycle,
            origin_url: window.location.origin,
            coupon_code: appliedCoupon?.code || null
          }, {
            headers: { Authorization: `Bearer ${freshToken}` }
          });
          
          setLoading(false);
          
          if (checkoutResponse.data.url) {
            // Redirect to Stripe checkout
            window.location.href = checkoutResponse.data.url;
            return;
          }
        } catch (error) {
          console.error('Failed to create checkout:', error);
          toast.error('Failed to start checkout. You can upgrade from settings later.');
        }
      }
      
      // If not skipping trial or checkout failed, just go to inbox
      // Store plan info for later if they want to upgrade
      if (selectedPlan && selectedPlan.monthly_price > 0 && !skipTrial) {
        sessionStorage.setItem('pendingPlan', JSON.stringify({
          planId: selectedPlan.id,
          billingCycle: billingCycle,
          couponCode: appliedCoupon?.code || null
        }));
      }
      
      setLoading(false);
      navigate('/inbox');
    } else {
      setLoading(false);
      setLocalError(result.error);
    }
  };

  const formatPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
    return `$${price}`;
  };

  const fullEmail = formData.username ? `${formData.username.toLowerCase()}${DOMAIN}` : '';

  // Step 1: Plan Selection
  if (step === 1) {
    return (
      <div className="min-h-screen bg-[#16162a] flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          {/* Logo - Links to Home */}
          <Link to="/" className="flex flex-col items-center mb-8 group">
            <img 
              src="/mpm-logo.png" 
              alt="MyPatriotMail" 
              className="w-20 h-20 object-contain mb-4 drop-shadow-[0_0_15px_rgba(109,74,255,0.5)] group-hover:scale-105 transition-transform"
            />
            <h1 className="text-2xl font-bold text-white group-hover:text-[#6D4AFF] transition-colors">MyPatriotMail</h1>
            <p className="text-gray-400 text-sm mt-1">Choose Your Plan</p>
          </Link>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center bg-[#1a1a2e] border border-[#2d2d44] rounded-full p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-[#6D4AFF] text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'yearly' 
                    ? 'bg-[#6D4AFF] text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs text-green-400">Save 17%</span>
              </button>
            </div>
          </div>

          {/* Skip Trial Toggle */}
          <div className="flex justify-center mb-8">
            <div 
              onClick={() => setSkipTrial(!skipTrial)}
              className={`inline-flex items-center gap-3 px-4 py-2 rounded-lg cursor-pointer transition-all ${
                skipTrial 
                  ? 'bg-[#6D4AFF]/20 border border-[#6D4AFF]' 
                  : 'bg-[#1a1a2e] border border-[#2d2d44] hover:border-[#6D4AFF]/50'
              }`}
            >
              <div className={`w-10 h-5 rounded-full p-0.5 transition-colors ${skipTrial ? 'bg-[#6D4AFF]' : 'bg-[#2d2d44]'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${skipTrial ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
              <div>
                <span className={`text-sm font-medium ${skipTrial ? 'text-[#6D4AFF]' : 'text-gray-400'}`}>
                  Skip Trial - Pay Now
                </span>
                <p className="text-xs text-gray-500">
                  {skipTrial ? 'You\'ll be charged immediately after registration' : 'Start with a free trial first'}
                </p>
              </div>
            </div>
          </div>

          {loadingPlans ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  className={`relative bg-[#1a1a2e] border rounded-2xl p-6 cursor-pointer transition-all ${
                    selectedPlan?.id === plan.id
                      ? 'border-[#6D4AFF] shadow-[0_0_30px_rgba(109,74,255,0.3)] scale-[1.02]'
                      : plan.is_featured
                      ? 'border-[#6D4AFF]/50'
                      : 'border-[#2d2d44] hover:border-[#6D4AFF]/50'
                  }`}
                >
                  {plan.is_featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#6D4AFF] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> POPULAR
                    </div>
                  )}
                  
                  {selectedPlan?.id === plan.id && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-[#6D4AFF] rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}

                  <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-black text-white">{formatPrice(plan)}</span>
                    <span className="text-gray-500">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </div>

                  <div className="space-y-2 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-[#6D4AFF]" />
                      <span>{plan.storage_limit_gb} GB Storage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#6D4AFF]" />
                      <span>{plan.emails_per_day} Emails/Day</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FolderPlus className="w-4 h-4 text-[#6D4AFF]" />
                      <span>{plan.custom_folders_limit} Custom Folders</span>
                    </div>
                    {plan.free_trial_enabled && (
                      <div className="flex items-center gap-2 text-green-400">
                        <Check className="w-4 h-4" />
                        <span>{plan.free_trial_days}-Day Free Trial</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Referral & Coupon Codes Section */}
          <div className="mt-8 max-w-md mx-auto space-y-4">
            {/* Referral Code */}
            <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-gray-300">Referral Code</span>
                <span className="text-xs text-gray-500">(from a friend)</span>
              </div>
              
              {referralApplied ? (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-medium text-sm">
                        {referralCode}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({referralSource === 'url' ? 'from link' : referralSource === 'cookie' ? 'remembered' : 'applied'})
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearReferral}
                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-gray-400 text-xs mt-2">
                    Your friend will receive credit for referring you!
                  </p>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={referralCodeManual}
                    onChange={(e) => setReferralCodeManual(e.target.value.toUpperCase())}
                    placeholder="Enter referral code"
                    className="bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-600 uppercase"
                  />
                  <Button
                    onClick={handleApplyReferral}
                    disabled={!referralCodeManual.trim()}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    Apply
                  </Button>
                </div>
              )}
            </div>

            {/* Discount Coupon */}
            <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Ticket className="w-4 h-4 text-[#6D4AFF]" />
                <span className="text-sm font-medium text-gray-300">Discount Coupon</span>
                <span className="text-xs text-gray-500">(for savings)</span>
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-600 uppercase"
                  disabled={!!appliedCoupon}
                />
                {appliedCoupon ? (
                  <Button
                    variant="outline"
                    onClick={clearCoupon}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleValidateCoupon}
                    disabled={!couponCode.trim() || couponValidating}
                    className="bg-[#6D4AFF] hover:bg-[#5B3FD9] disabled:opacity-50"
                  >
                    {couponValidating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                )}
              </div>
              
              {couponError && (
                <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {couponError}
                </p>
              )}
              
              {appliedCoupon && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-medium text-sm">
                        {appliedCoupon.code} applied!
                      </span>
                    </div>
                    <span className="text-green-400 font-bold">
                      {appliedCoupon.discount_type === 'percentage' 
                        ? `-${appliedCoupon.discount_value}%`
                        : `-$${appliedCoupon.discount_value.toFixed(2)}`
                      }
                    </span>
                  </div>
                  {appliedCoupon.discount_amount && (
                    <p className="text-gray-400 text-xs mt-1">
                      You save ${appliedCoupon.discount_amount.toFixed(2)} on this plan
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-[#2d2d44] text-gray-400 hover:bg-[#2d2d44] hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={() => setStep(2)}
              disabled={!selectedPlan}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9] disabled:opacity-50"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#6D4AFF] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Create Account
  return (
    <div className="min-h-screen bg-[#16162a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - Links to Home */}
        <Link to="/" className="flex flex-col items-center mb-8 group">
          <img 
            src="/mpm-logo.png" 
            alt="MyPatriotMail" 
            className="w-20 h-20 object-contain mb-4 drop-shadow-[0_0_15px_rgba(109,74,255,0.5)] group-hover:scale-105 transition-transform"
          />
          <h1 className="text-2xl font-bold text-white group-hover:text-[#6D4AFF] transition-colors">MyPatriotMail</h1>
          <p className="text-gray-400 text-sm mt-1">Create your secure email account</p>
        </Link>

        {/* Selected Plan Badge */}
        {selectedPlan && (
          <div className="bg-[#1a1a2e] border border-[#6D4AFF]/50 rounded-xl p-4 mb-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Selected Plan</p>
              <p className="text-white font-medium">{selectedPlan.name} - {formatPrice(selectedPlan)}/{billingCycle === 'yearly' ? 'year' : 'month'}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep(1)}
              className="text-[#6D4AFF] hover:text-white hover:bg-[#6D4AFF]/20"
            >
              Change
            </Button>
          </div>
        )}

        {/* Skip Trial Toggle - Step 2 */}
        {selectedPlan && (
          <div 
            onClick={() => setSkipTrial(!skipTrial)}
            className={`mb-6 flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all ${
              skipTrial 
                ? 'bg-[#6D4AFF]/20 border-2 border-[#6D4AFF]' 
                : 'bg-[#1a1a2e] border border-[#2d2d44] hover:border-[#6D4AFF]/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-6 rounded-full p-0.5 transition-colors ${skipTrial ? 'bg-[#6D4AFF]' : 'bg-[#2d2d44]'}`}>
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${skipTrial ? 'translate-x-6' : 'translate-x-0'}`} />
              </div>
              <div>
                <p className={`font-medium ${skipTrial ? 'text-[#6D4AFF]' : 'text-white'}`}>
                  {skipTrial ? '💳 Pay Now - Skip Trial' : '🎁 Start Free Trial'}
                </p>
                <p className="text-xs text-gray-400">
                  {skipTrial 
                    ? 'You\'ll be charged immediately after registration' 
                    : 'Leave here to start your free trial today'
                  }
                </p>
              </div>
            </div>
            {skipTrial && (
              <span className="text-[#6D4AFF] text-sm font-medium">
                {formatPrice(selectedPlan)}
              </span>
            )}
          </div>
        )}

        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-white">Create account</CardTitle>
            <CardDescription className="text-gray-400">
              Choose your @mypatriotmail.org username
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(localError || error) && (
                <Alert variant="destructive" className="bg-red-500/10 border-red-500/50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{localError || error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="displayName" className="text-gray-300">Display Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                  <Input
                    id="displayName"
                    name="displayName"
                    type="text"
                    placeholder="John Doe"
                    value={formData.displayName}
                    onChange={handleChange}
                    className="pl-10 bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500 focus-visible:ring-[#6D4AFF]"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-300">Email Address</Label>
                <div className="flex">
                  <div className="relative flex-1">
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="username"
                      value={formData.username}
                      onChange={handleChange}
                      className="rounded-r-none bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500 focus-visible:ring-[#6D4AFF] pr-10"
                      required
                    />
                    <div className="absolute right-3 top-3">
                      {checkingAvailability && (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      )}
                      {!checkingAvailability && usernameAvailable === true && (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                      {!checkingAvailability && usernameAvailable === false && (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                  <div className="bg-[#6D4AFF]/30 border border-l-0 border-[#6D4AFF]/60 rounded-r-lg px-3 flex items-center">
                    <span className="text-white text-sm font-semibold whitespace-nowrap">{DOMAIN}</span>
                  </div>
                </div>
                {usernameMessage && (
                  <p className={`text-xs flex items-center gap-1 ${usernameAvailable ? 'text-green-500' : 'text-red-400'}`}>
                    {usernameAvailable ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    {usernameMessage}
                  </p>
                )}
                {formData.username && (
                  <p className="text-xs text-gray-500">
                    Your email will be: <span className="text-gray-300">{fullEmail}</span>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500 focus-visible:ring-[#6D4AFF]"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-500 focus-visible:ring-[#6D4AFF]"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <Check className={`w-3 h-3 ${req.met ? 'text-green-500' : 'text-gray-500'}`} />
                    <span className={req.met ? 'text-green-500' : 'text-gray-500'}>{req.text}</span>
                  </div>
                ))}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white"
                disabled={loading || checkingAvailability || usernameAvailable === false}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <div className="text-sm text-gray-400 text-center">
              Already have an account?{' '}
              <Link to="/login" className="text-[#6D4AFF] hover:underline">
                Sign in
              </Link>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Lock className="w-3 h-3" />
              <span>Your data is encrypted with AES-256</span>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
